import i18n from 'i18next'
import { HELP_CONTENT_EN } from './content-en'
import type { HelpPage } from './types'

export const HELP_CONTENT: Record<string, HelpPage> = {
  // ============================================================
  // MON ESPACE (self-service)
  // ============================================================

  '/my': {
    title: 'Mon espace',
    summary:
      'Votre tableau de bord personnel : pointage du jour, soldes de congés, et derniers bulletins de paie.',
    steps: [
      {
        label: 'Pointer votre entrée',
        detail: 'Cliquez sur "Pointer l\'entrée" au début de votre journée.',
      },
      {
        label: 'Consulter vos soldes',
        detail: 'Les barres de progression montrent vos congés acquis et pris.',
      },
      {
        label: 'Demander un congé',
        detail: 'Bouton "Demander" → choisir type, dates, motif.',
      },
      {
        label: 'Pointer votre sortie',
        detail: "Même bouton en fin de journée pour enregistrer l'heure de sortie.",
      },
    ],
    faq: [
      {
        q: "Mon pointage n'apparaît pas, que faire ?",
        a: 'Vérifiez votre connexion internet, puis rechargez la page. Si le problème persiste, contactez votre responsable RH pour un ajustement manuel.',
      },
      {
        q: 'Je ne vois pas mes bulletins de paie',
        a: 'Les bulletins sont visibles dès que la période de paie est validée par le service paie. Si une période est manquante, contactez le service paie.',
      },
      {
        q: 'Comment annuler une demande de congé ?',
        a: 'Depuis l\'onglet "Mes congés", cliquer sur la demande → bouton "Annuler". Possible uniquement si le statut est encore "en attente".',
      },
    ],
    relatedLinks: [
      { label: 'Mes congés', path: '/my/leaves' },
      { label: 'Mes bulletins', path: '/my/payslips' },
      { label: 'Mon profil', path: '/my/profile' },
    ],
  },

  '/my/leaves': {
    title: 'Mes congés',
    summary:
      "Consultez vos soldes de congés et soumettez vos demandes. Les demandes suivent un workflow d'approbation.",
    steps: [
      {
        label: 'Choisir le type de congé',
        detail: 'Congés payés, maladie, événement familial, etc.',
      },
      {
        label: 'Sélectionner les dates',
        detail: 'Les week-ends et jours fériés sont automatiquement exclus du calcul.',
      },
      {
        label: 'Indiquer le motif',
        detail: 'Obligatoire pour certains types (maladie, événement familial).',
      },
      {
        label: "Suivre l'approbation",
        detail: 'Notification automatique à votre manager puis aux RH.',
      },
    ],
    fields: [
      {
        name: 'Jours ouvrables',
        definition:
          'Nombre de jours comptés hors week-ends et jours fériés. Ex: du lundi au vendredi = 5 jours ouvrables.',
      },
      {
        name: 'Solde disponible',
        definition: "Jours acquis — jours pris — jours en attente d'approbation.",
      },
      {
        name: 'Demi-journée',
        definition: 'Cochez "demi-journée début" ou "demi-journée fin" pour une prise de 0.5 jour.',
      },
    ],
    faq: [
      {
        q: 'Pourquoi mon solde ne change pas après une approbation ?',
        a: "Le solde est mis à jour dès l'approbation finale (manager ET RH si requis). Si les deux ont approuvé et le solde n'a pas changé, contactez les RH.",
      },
      {
        q: 'Peut-on prendre des congés au-delà du solde ?',
        a: 'Non, sauf pour le congé sans solde. Le système bloque les demandes si le solde est insuffisant.',
      },
    ],
    warnings: [
      'Toute demande approuvée puis annulée après la date de début doit être traitée manuellement par les RH.',
    ],
  },

  '/my/payslips': {
    title: 'Mes bulletins de paie',
    summary:
      'Consultez et téléchargez vos bulletins de paie mensuels. Les bulletins sont disponibles dès validation de la période.',
    faq: [
      {
        q: 'Comment lire mon bulletin ?',
        a: 'Section "Gains" : votre salaire brut et primes. Section "Cotisations" : ce qui est déduit (IPRES, IRPP). "Net à payer" : le montant viré sur votre compte.',
      },
      {
        q: "Qu'est-ce que l'IPRES ?",
        a: 'Institut de Prévoyance Retraite du Sénégal. Cotisation retraite prélevée sur votre salaire brut (5.6% plafonné).',
      },
      {
        q: "Qu'est-ce que l'IRPP ?",
        a: 'Impôt sur le Revenu des Personnes Physiques. Calculé selon un barème progressif, avec abattements pour personnes à charge.',
      },
      {
        q: 'Que faire si je constate une erreur sur mon bulletin ?',
        a: 'Contactez le service paie immédiatement. Les corrections sont effectuées sur la période suivante par une ligne de régularisation.',
      },
    ],
  },

  '/my/profile': {
    title: 'Mon profil',
    summary:
      "Gérez vos informations personnelles, changez votre mot de passe et configurez vos préférences d'affichage.",
    steps: [
      {
        label: 'Modifier votre profil',
        detail: "Nom, email, téléphone. L'email est utilisé pour les notifications.",
      },
      {
        label: 'Changer le mot de passe',
        detail: 'Minimum 10 caractères avec majuscule, minuscule et chiffre.',
      },
      {
        label: 'Choisir votre langue',
        detail: "Français ou Anglais. Affecte l'interface et les notifications.",
      },
    ],
    warnings: [
      'Le changement de mot de passe peut déconnecter automatiquement vos autres sessions actives.',
    ],
  },

  '/my/attendance': {
    title: 'Mes pointages',
    summary:
      "Historique de vos pointages d'entrée et de sortie. Permet de pointer en libre-service depuis l'interface web.",
    faq: [
      {
        q: "J'ai oublié de pointer ma sortie hier",
        a: "Contactez votre responsable RH pour un ajustement manuel. Le motif sera enregistré dans le journal d'audit.",
      },
    ],
  },

  // ============================================================
  // ADMINISTRATION (paramètres)
  // ============================================================

  '/settings/users': {
    title: 'Utilisateurs',
    summary:
      'Gérez les comptes utilisateurs de votre organisation : inviter, activer, désactiver, attribuer des rôles.',
    steps: [
      {
        label: 'Inviter un utilisateur',
        detail: 'Bouton "Inviter" → email + rôles → lien valable 72h.',
      },
      {
        label: 'Attribuer des rôles',
        detail: 'Fiche utilisateur → onglet "Rôles" → Attribuer.',
      },
      {
        label: 'Désactiver un compte',
        detail:
          'Actions → Désactiver. Le compte ne peut plus se connecter mais ses données sont conservées.',
      },
      {
        label: 'Débloquer un compte',
        detail: 'Si un user a été verrouillé après 5 tentatives, utiliser "Débloquer".',
      },
    ],
    fields: [
      {
        name: 'Rôle',
        definition:
          "Détermine les permissions dans l'application. Un user peut avoir plusieurs rôles cumulés.",
      },
      {
        name: 'Statut actif',
        definition:
          'Compte actif = peut se connecter. Désactivé = connexion bloquée mais données conservées.',
      },
      {
        name: 'Dernière connexion',
        definition: 'Date et IP de la dernière connexion réussie.',
      },
    ],
    faq: [
      {
        q: "L'invitation n'a pas été reçue ?",
        a: 'Vérifiez les spams. Utilisez "Renvoyer l\'invitation" sur la fiche. Le lien expire après 72h.',
      },
      {
        q: 'Peut-on supprimer définitivement un compte ?',
        a: 'Non. Les comptes sont désactivés, jamais supprimés (traçabilité des opérations). Contactez le support pour une suppression RGPD.',
      },
    ],
    warnings: [
      'Un utilisateur doit toujours avoir au moins un rôle. La suppression du dernier rôle est bloquée.',
    ],
    relatedLinks: [
      { label: 'Préférences', path: '/settings/preferences' },
      { label: 'Organisation', path: '/settings/organization' },
    ],
  },

  '/settings/organization': {
    title: 'Organisation',
    summary:
      'Informations légales de votre organisation : nom, NINEA, adresse, RC. Ces données apparaissent sur tous les documents générés.',
    faq: [
      {
        q: "Comment modifier le nom et NINEA de l'organisation ?",
        a: 'Saisir directement les nouvelles valeurs et enregistrer. Ces données apparaissent sur les factures, bulletins et déclarations.',
      },
    ],
    warnings: [
      'Les modifications de paramètres légaux (NINEA, adresse) affectent tous les documents générés à partir de la date de modification.',
    ],
  },

  '/settings': {
    title: 'Paramètres',
    summary:
      'Configuration générale de votre organisation : informations légales, paramètres comptables, RH, plan de comptes, tarifs et utilisateurs.',
    relatedLinks: [
      { label: 'Organisation', path: '/settings/organization' },
      { label: 'Utilisateurs', path: '/settings/users' },
      { label: 'Préférences', path: '/settings/preferences' },
      { label: 'Plan comptable', path: '/settings/chart-of-accounts' },
    ],
  },

  // ============================================================
  // RESSOURCES HUMAINES
  // ============================================================

  '/hr': {
    title: 'Dashboard RH',
    summary:
      "Vue d'ensemble des ressources humaines : effectif actif, congés en cours, demandes en attente, contrats échus.",
    faq: [
      {
        q: 'Comment créer un premier employé ?',
        a: 'Menu "Employés" → "Nouvel employé". Renseignez identité + date d\'embauche. Créez ensuite un contrat depuis la fiche employé.',
      },
      {
        q: 'Que signifie "contrats échus dans 30j" ?',
        a: 'CDD ou contrats de stage dont la date de fin arrive dans moins de 30 jours. Action requise : renouveler ou clôturer.',
      },
    ],
    relatedLinks: [
      { label: 'Liste employés', path: '/hr/employees' },
      { label: 'Congés', path: '/hr/leaves' },
      { label: 'Calendrier', path: '/hr/leaves/calendar' },
    ],
  },

  '/hr/employees': {
    title: 'Employés',
    summary:
      'Liste complète des employés actifs et archivés. Chaque fiche regroupe identité, contrat, congés et présences.',
    steps: [
      {
        label: 'Créer un employé',
        detail: 'Bouton "Nouvel employé" → identité, département, poste, date d\'embauche.',
      },
      {
        label: 'Créer son contrat',
        detail: 'Depuis la fiche → onglet "Contrat" → "Nouveau contrat".',
      },
      {
        label: 'Lier à un compte utilisateur',
        detail:
          "Si l'employé a un compte AMANET, le lier dans la fiche pour activer le self-service.",
      },
    ],
    fields: [
      {
        name: 'Code employé',
        definition:
          'Identifiant unique généré automatiquement (EMP-0001). Utilisé pour les bulletins et déclarations sociales.',
      },
      {
        name: 'IPRES',
        definition:
          "Numéro d'immatriculation IPRES (retraite). Obligatoire pour les déclarations mensuelles.",
      },
      {
        name: 'CSS',
        definition:
          'Numéro Caisse de Sécurité Sociale. Obligatoire pour les déclarations AT/prestations familiales.',
      },
      {
        name: 'Personnes à charge',
        definition:
          "Nombre d'enfants et personnes à charge. Utilisé pour le calcul de l'abattement IRPP.",
      },
    ],
    faq: [
      {
        q: 'Peut-on avoir deux contrats actifs pour un même employé ?',
        a: 'Non. Un seul contrat actif par employé. Pour changer de contrat, terminer le précédent et créer le nouveau.',
      },
      {
        q: 'Comment enregistrer une démission ou un licenciement ?',
        a: 'Fiche employé → Actions → "Terminer le contrat". Indiquer la raison et la date effective.',
      },
    ],
    warnings: [
      'La suppression d\'un employé est impossible. Utilisez "Statut : Terminé" pour archiver.',
    ],
  },

  '/hr/employees/new': {
    title: 'Nouvel employé',
    summary:
      "Création d'un dossier employé. Renseignez au minimum l'identité et la date d'embauche. Le contrat se crée dans la fiche.",
    steps: [
      {
        label: 'Informations obligatoires',
        detail: "Prénom, nom, date d'embauche. Le reste peut être complété plus tard.",
      },
      {
        label: 'Identifiants sociaux',
        detail: 'Numéros IPRES et CSS — nécessaires pour la paie.',
      },
      {
        label: 'Personnes à charge',
        detail: "Impacte directement le calcul de l'IRPP.",
      },
      {
        label: 'Lier un compte utilisateur',
        detail: 'Optionnel — permet le self-service (congés, bulletins).',
      },
    ],
    fields: [
      {
        name: "Date d'embauche",
        definition: "Détermine l'ancienneté et le prorata des congés la première année.",
      },
      {
        name: 'Statut matrimonial',
        definition: 'Célibataire = 1 part fiscale. Marié = 2 parts. Impacte le calcul IRPP.',
      },
    ],
  },

  '/hr/contracts': {
    title: 'Contrats',
    summary:
      'Gestion des contrats de travail. Types disponibles : CDI, CDD, Stage, Apprentissage, Intérim.',
    fields: [
      {
        name: 'CDI',
        definition:
          'Contrat à Durée Indéterminée. Sans date de fin. Résiliable par démission ou licenciement.',
      },
      {
        name: 'CDD',
        definition:
          'Contrat à Durée Déterminée. 2 renouvellements maximum au Sénégal. Au-delà = CDI automatique.',
      },
      {
        name: "Période d'essai",
        definition:
          'Période durant laquelle les deux parties peuvent rompre sans préavis. Durée variable selon la convention.',
      },
      {
        name: 'Salaire de base',
        definition:
          "Montant brut mensuel sur lequel sont calculées les cotisations IPRES, CSS et l'IRPP.",
      },
      {
        name: 'Avenant',
        definition:
          "Modification d'un contrat existant (augmentation, changement de poste). Trace les valeurs avant/après.",
      },
    ],
    faq: [
      {
        q: 'Comment enregistrer une augmentation de salaire ?',
        a: 'Fiche contrat → "Créer un avenant" → type "Changement de salaire". L\'historique des avenants est conservé.',
      },
    ],
    warnings: ['Un contrat validé est immuable. Toute modification passe par un avenant horodaté.'],
  },

  '/hr/leaves': {
    title: 'Congés — Administration',
    summary:
      "Gestion des demandes de congés de tous les employés. Approuvez, refusez, et consultez le calendrier d'absences.",
    steps: [
      {
        label: 'Consulter les demandes en attente',
        detail: 'Filtre "En attente manager" ou "En attente RH".',
      },
      {
        label: 'Approuver une demande',
        detail: 'Cliquer sur la demande → "Approuver". Un commentaire est optionnel.',
      },
      {
        label: 'Refuser avec motif',
        detail: '"Refuser" → saisir la raison. L\'employé est notifié et son solde restauré.',
      },
      {
        label: 'Créer une demande pour un employé',
        detail: 'Bouton "Nouvelle demande" → sélectionner l\'employé.',
      },
    ],
    faq: [
      {
        q: 'Quand le solde est-il débité ?',
        a: 'Le solde est "réservé" dès la soumission (statut pending). Il est définitivement débité à l\'approbation finale.',
      },
      {
        q: "Comment fonctionne le double niveau d'approbation ?",
        a: "Certains types (maladie, maternité) nécessitent approbation manager PUIS RH. D'autres (événements familiaux) vont directement aux RH.",
      },
    ],
    relatedLinks: [{ label: 'Calendrier des congés', path: '/hr/leaves/calendar' }],
  },

  // ============================================================
  // COMPTABILITÉ (M09 SYSCOHADA)
  // ============================================================

  '/accounting/trial-balance': {
    title: 'Balance générale',
    summary:
      'Tableau récapitulatif de tous les comptes avec leurs totaux débit, crédit et solde à une date donnée. Conforme SYSCOHADA.',
    fields: [
      {
        name: 'Débit cumulé',
        definition: "Total des mouvements débiteurs sur le compte depuis le début de l'exercice.",
      },
      {
        name: 'Crédit cumulé',
        definition: "Total des mouvements créditeurs sur le compte depuis le début de l'exercice.",
      },
      {
        name: 'Solde',
        definition:
          'Débit — Crédit pour les comptes à solde débiteur (actif/charges). Crédit — Débit pour les comptes à solde créditeur (passif/produits).',
      },
      {
        name: 'Balance équilibrée',
        definition:
          "La somme des débits doit TOUJOURS égaler la somme des crédits. C'est la règle fondamentale de la partie double.",
      },
    ],
    faq: [
      {
        q: 'Que signifie "déséquilibre" affiché en bas de la balance ?',
        a: "Erreur critique. Cela signifie qu'une écriture comptable a été créée sans respecter la partie double. Contactez IMPACT SOLUTIONS.",
      },
      {
        q: 'Comment filtrer par classe de compte ?',
        a: 'Cliquez sur les boutons "Classe 1" à "Classe 8" au-dessus du tableau.',
      },
      {
        q: 'Cliquer sur un compte — que se passe-t-il ?',
        a: 'Ouvre le grand livre de ce compte : liste de tous les mouvements avec solde progressif.',
      },
    ],
    relatedLinks: [
      { label: 'Compte de résultat', path: '/accounting/income-statement' },
      { label: 'Bilan', path: '/accounting/balance-sheet' },
    ],
  },

  '/accounting/income-statement': {
    title: 'Compte de résultat',
    summary:
      'État financier comparant produits (classe 7) et charges (classe 6) sur une période. Le solde est le bénéfice ou la perte.',
    fields: [
      {
        name: 'Produits (classe 7)',
        definition:
          "Revenus générés par l'activité : prestations fret (706000), autres produits (75x, 77x).",
      },
      {
        name: 'Charges (classe 6)',
        definition:
          "Coûts d'exploitation : salaires (64x), sous-traitance (611000), carburant (624000), amortissements (68x).",
      },
      {
        name: 'Résultat net',
        definition:
          'Produits — Charges. Positif = bénéfice. Négatif = perte. Repris dans le bilan au compte 130000.',
      },
    ],
    faq: [
      {
        q: 'Les factures non payées apparaissent-elles dans le résultat ?',
        a: "Oui. La comptabilité d'engagement enregistre les produits à la date de facturation, indépendamment du paiement.",
      },
    ],
    warnings: [
      'Les chiffres reflètent uniquement les écritures VALIDÉES. Les brouillons ne sont pas inclus.',
    ],
  },

  '/accounting/balance-sheet': {
    title: 'Bilan',
    summary:
      "Photographie du patrimoine de l'entreprise à une date : actif (ce que l'on possède) vs passif (ce que l'on doit).",
    fields: [
      {
        name: 'Actif',
        definition:
          'Classes 2 (immobilisations), 3 (stocks), 4 débit (créances clients), 5 (trésorerie).',
      },
      {
        name: 'Passif',
        definition:
          'Classes 1 (capitaux propres), 4 crédit (dettes fournisseurs, sociales, fiscales).',
      },
      {
        name: 'Équilibre',
        definition:
          "Total actif = Total passif + Capitaux propres. C'est une identité comptable. Un déséquilibre indique une erreur.",
      },
    ],
    faq: [
      {
        q: 'Comment les factures clients impactent-elles le bilan ?',
        a: "Les factures émises augmentent le compte 411000 (Clients) à l'actif. Le paiement reçu réduit 411 et augmente 521 (Banque).",
      },
    ],
  },

  '/accounting/entries': {
    title: 'Journal des écritures',
    summary:
      'Toutes les écritures comptables : automatiques (depuis les factures et la paie) et manuelles. Classées par journal.',
    fields: [
      {
        name: 'Journal VT',
        definition:
          'Journal des ventes. Écritures générées automatiquement depuis les factures clients (M03).',
      },
      {
        name: 'Journal BQ',
        definition: "Journal banque. Écritures de règlement (paiement reçu d'un client).",
      },
      {
        name: 'Journal PA',
        definition:
          "Journal paie. Écritures mensuelles générées à la validation d'une période de paie.",
      },
      {
        name: 'Journal OD',
        definition: 'Opérations Diverses. Pour les saisies manuelles de régularisation.',
      },
      {
        name: 'Écriture validée',
        definition:
          'Écriture définitive, immuable. Ne peut PAS être modifiée. Correction = extourne.',
      },
      {
        name: 'Extourne',
        definition:
          "Écriture inverse d'une écriture validée. Annule l'effet comptable sans supprimer l'historique.",
      },
    ],
    faq: [
      {
        q: 'Pourquoi ne puis-je pas modifier une écriture validée ?',
        a: 'C\'est une règle SYSCOHADA (immuabilité des écritures). Pour corriger, utilisez "Extourner" puis créez une nouvelle écriture correcte.',
      },
    ],
    warnings: [
      'Une écriture manuelle doit toujours être équilibrée (débit = crédit) avant validation. Le système bloque les écritures déséquilibrées.',
    ],
  },

  '/accounting/entries/new': {
    title: 'Nouvelle écriture manuelle',
    summary:
      "Saisie d'une écriture comptable manuelle (OD). À utiliser pour les régularisations, provisions, ou corrections.",
    steps: [
      {
        label: 'Choisir le journal',
        detail: 'OD pour les opérations diverses, BQ si lié à la banque.',
      },
      {
        label: 'Saisir le libellé',
        detail: 'Description claire obligatoire (ex: "Provision congés Q2 2026").',
      },
      {
        label: 'Ajouter les lignes',
        detail: 'Au moins 2 lignes. Chaque ligne = 1 compte. Tapez le code ou cherchez par nom.',
      },
      {
        label: "Vérifier l'équilibre",
        detail:
          'Total débit = Total crédit obligatoire. Le bouton "Valider" reste désactivé tant que c\'est déséquilibré.',
      },
    ],
    fields: [
      {
        name: 'Compte (code HS)',
        definition:
          'Code SYSCOHADA à 6 chiffres. Ex: 411000 = Clients, 521000 = Banque, 706000 = Prestations fret.',
      },
      {
        name: 'Débit vs Crédit',
        definition:
          "Débit = entrée sur un compte d'actif/charge. Crédit = entrée sur un compte de passif/produit. Une ligne ne peut pas avoir les deux.",
      },
    ],
    warnings: [
      'Les écritures sont validées et immuables. Vérifiez bien avant de valider.',
      "N'utilisez jamais une écriture manuelle pour des opérations déjà générées automatiquement (factures, paie). Risque de doublon.",
    ],
    relatedLinks: [{ label: 'Plan comptable', path: '/settings/chart-of-accounts' }],
  },

  // ============================================================
  // FINANCE (M03 — Facturation AR)
  // ============================================================

  '/finance': {
    title: 'Dashboard Finance',
    summary:
      'Vue synthétique : encaissements du mois, créances en cours, factures en retard, indicateurs de trésorerie.',
    faq: [
      {
        q: 'Que signifie "AR" ?',
        a: 'Accounts Receivable — Créances clients. Montants facturés mais pas encore encaissés.',
      },
      {
        q: 'Comment voir les factures en retard de paiement ?',
        a: 'Section "Retards" du dashboard, ou liste factures filtrée par statut "En retard".',
      },
    ],
    relatedLinks: [
      { label: 'Factures', path: '/finance/invoices' },
      { label: 'Créances (AR)', path: '/finance/ar' },
      { label: 'Balance comptable', path: '/accounting/trial-balance' },
    ],
  },

  '/finance/invoices': {
    title: 'Factures clients',
    summary:
      "Gestion du cycle de facturation : de la création à l'encaissement. Les factures sont générées automatiquement depuis les bookings.",
    fields: [
      {
        name: 'Facture brouillon',
        definition: "En préparation. Modifiable. N'a pas encore d'impact comptable.",
      },
      {
        name: 'Facture émise',
        definition:
          'Envoyée au client. Écriture comptable générée (DR 411 / CR 706 / CR 445810). Immuable.',
      },
      {
        name: 'Facture payée',
        definition: 'Paiement reçu et alloué. Compte client (411) soldé.',
      },
      {
        name: 'Avoir',
        definition:
          "Annulation partielle ou totale d'une facture émise. Génère une écriture d'extourne.",
      },
      {
        name: 'TVA 18%',
        definition:
          "Taxe sur la Valeur Ajoutée. Collectée pour le compte de l'État sénégalais. Compte 445810.",
      },
    ],
    faq: [
      {
        q: 'Peut-on modifier une facture émise ?',
        a: 'Non. Pour corriger, créez un avoir sur la facture erronée, puis une nouvelle facture corrigée.',
      },
      {
        q: 'Comment enregistrer un paiement partiel ?',
        a: 'Dans la liste des paiements → "Nouveau paiement" → allouer le montant reçu à la facture concernée.',
      },
    ],
    warnings: [
      "L'émission d'une facture génère automatiquement une écriture comptable. Cette action est irréversible sans création d'un avoir.",
    ],
  },

  '/finance/payments/new': {
    title: 'Nouveau paiement',
    summary:
      "Enregistrement d'un encaissement client. Chaque paiement est alloué à une ou plusieurs factures et génère une écriture bancaire.",
    steps: [
      {
        label: 'Saisir le montant et la méthode',
        detail: 'Montant reçu, méthode de paiement, date, référence.',
      },
      {
        label: 'Allouer aux factures',
        detail: 'Sélectionner la ou les factures concernées par ce paiement.',
      },
      {
        label: "Vérifier l'écriture",
        detail: 'DR 521000 (Banque) / CR 411000 (Clients) générée automatiquement.',
      },
    ],
    fields: [
      {
        name: 'Référence virement',
        definition:
          'Numéro de référence du virement bancaire ou du chèque. Pour rapprochement avec le relevé bancaire.',
      },
      {
        name: 'Allocation',
        definition:
          'Association entre un paiement et une ou plusieurs factures. Un paiement peut solder plusieurs factures.',
      },
    ],
  },

  // ============================================================
  // PAIE (M12)
  // ============================================================

  '/payroll': {
    title: 'Dashboard Paie',
    summary:
      "Vue d'ensemble des périodes de paie. Chaque mois = une période à calculer, valider, et payer.",
    faq: [
      {
        q: 'Dans quel ordre traiter une période de paie ?',
        a: 'Créer → Calculer → Vérifier → Valider → Payer → Clôturer. Chaque étape est irréversible.',
      },
    ],
    relatedLinks: [{ label: 'Périodes de paie', path: '/payroll/periods' }],
  },

  '/payroll/periods': {
    title: 'Périodes de paie',
    summary:
      'Gestion du cycle mensuel de paie. Chaque période couvre un mois et génère un bulletin par employé actif.',
    steps: [
      {
        label: 'Créer la période',
        detail:
          'Bouton "Nouvelle période" → sélectionner mois/année. Une seule période possible par mois.',
      },
      {
        label: 'Calculer les bulletins',
        detail:
          'Bouton "Calculer" — le système calcule automatiquement IPRES, CSS, IRPP, Trimf pour chaque employé actif.',
      },
      {
        label: 'Vérifier les bulletins',
        detail:
          'Consulter chaque bulletin généré. Si erreur : recalculer (possible avant validation).',
      },
      {
        label: 'Valider et comptabiliser',
        detail:
          'Bouton "Valider" — génère l\'écriture comptable (journal PA) dans M09. Irréversible.',
      },
      {
        label: 'Marquer comme payée',
        detail: 'Après virement des salaires sur les comptes des employés.',
      },
      {
        label: 'Clôturer',
        detail: 'Période verrouillée définitivement. Aucune modification possible.',
      },
    ],
    fields: [
      {
        name: 'Masse salariale brute',
        definition:
          'Somme de tous les salaires bruts + primes. Base de calcul des cotisations patronales.',
      },
      {
        name: 'Net total',
        definition: 'Somme des nets à payer. Montant total à virer aux employés.',
      },
      {
        name: 'Coût total entreprise',
        definition: "Masse brute + charges patronales (IPRES + CSS). Coût réel de la main-d'œuvre.",
      },
    ],
    faq: [
      {
        q: 'Que se passe-t-il si un employé a été créé en cours de mois ?',
        a: "Le calcul est proratisé automatiquement selon la date d'embauche. Vérifiez le bulletin individuel.",
      },
      {
        q: 'Comment corriger une erreur après validation ?',
        a: "Annuler le bulletin concerné → créer un bulletin de régularisation sur la période suivante avec une ligne d'écart.",
      },
    ],
    warnings: [
      "La validation d'une période est IRRÉVERSIBLE. Elle génère une écriture comptable définitive dans le journal PA.",
      'Vérifiez que tous les contrats sont à jour AVANT de calculer la période.',
    ],
  },

  '/payroll/analytics': {
    title: 'Analytique paie',
    summary:
      'Vue analytique de la masse salariale, des charges sociales et de leur évolution dans le temps.',
    fields: [
      {
        name: 'Évolution masse brute',
        definition: 'Variation de la somme des salaires bruts mois après mois. Indicateur RH clé.',
      },
      {
        name: 'Charges patronales',
        definition: 'IPRES + CSS + AT employeur. Dépend de la masse brute.',
      },
    ],
  },

  '/payroll/declarations': {
    title: 'Déclarations sociales',
    summary:
      'Génération mensuelle/trimestrielle des déclarations IPRES, CSS, IRPP destinées aux organismes sociaux et fiscaux.',
    fields: [
      {
        name: 'Déclaration IPRES',
        definition: "État mensuel détaillant l'assiette et les cotisations retraite par employé.",
      },
      {
        name: 'Déclaration CSS',
        definition: 'État mensuel des cotisations prestations familiales et accidents du travail.',
      },
      {
        name: 'Déclaration IRPP',
        definition: "Reversement mensuel de l'impôt sur le revenu prélevé à la source.",
      },
    ],
    warnings: [
      'Une fois transmises aux organismes, les déclarations ne peuvent être corrigées que par dépôt rectificatif.',
    ],
  },

  // ============================================================
  // DASHBOARD
  // ============================================================

  '/dashboard': {
    title: 'Tableau de bord',
    summary:
      "Vue d'ensemble de votre activité : indicateurs opérationnels, financiers et RH du mois en cours.",
    faq: [
      {
        q: 'Comment changer la période affichée ?',
        a: 'Utilisez le sélecteur de période en haut du dashboard (mois, trimestre, année).',
      },
    ],
    relatedLinks: [
      { label: 'Finance', path: '/finance' },
      { label: 'RH', path: '/hr' },
      { label: 'Comptabilité', path: '/accounting' },
    ],
  },

  // ============================================================
  // COMPTABILITÉ — additions
  // ============================================================

  '/accounting': {
    title: 'Comptabilité',
    summary:
      'Tableau de bord comptable : résultat du mois, trésorerie, créances/dettes en cours et accès rapide aux états financiers SYSCOHADA.',
    relatedLinks: [
      { label: 'Balance générale', path: '/accounting/trial-balance' },
      { label: 'Compte de résultat', path: '/accounting/income-statement' },
      { label: 'Bilan', path: '/accounting/balance-sheet' },
      { label: 'Journal des écritures', path: '/accounting/entries' },
      { label: 'Rapprochement bancaire', path: '/accounting/reconciliation' },
      { label: 'TVA', path: '/accounting/vat' },
    ],
  },

  '/accounting/reconciliation': {
    title: 'Rapprochement bancaire',
    summary:
      'Réconciliation des écritures comptables avec les relevés bancaires. Garantit que le solde du compte 521 reflète la réalité de la trésorerie.',
    steps: [
      {
        label: 'Importer le relevé',
        detail: 'Format CSV ou OFX fourni par votre banque.',
      },
      {
        label: 'Lancer le matching automatique',
        detail:
          'Le système associe les lignes du relevé aux écritures comptables par montant, date et référence.',
      },
      {
        label: 'Traiter les écarts',
        detail:
          'Lignes non rapprochées : créer une écriture manquante, marquer comme frais bancaires, ou demander une enquête.',
      },
      {
        label: 'Valider le rapprochement',
        detail: 'Une fois 100% des lignes traitées, valider pour clôturer la période.',
      },
    ],
    fields: [
      {
        name: 'Solde théorique',
        definition: 'Solde du compte 521 dans la balance comptable à la date du relevé.',
      },
      {
        name: 'Solde bancaire',
        definition: 'Solde affiché par la banque sur le relevé.',
      },
      {
        name: 'Écart',
        definition:
          'Différence entre solde théorique et solde bancaire. Doit être expliqué par les éléments en cours (chèques émis non débités, virements en transit).',
      },
    ],
    faq: [
      {
        q: 'Comment importer un relevé ?',
        a: 'Bouton "Nouveau rapprochement" → choisir le compte bancaire → uploader le fichier CSV/OFX.',
      },
      {
        q: 'Le système ne reconnaît pas une ligne du relevé',
        a: "C'est probablement une opération bancaire (frais, agios) sans écriture comptable. Créez l'écriture manquante depuis la ligne non matchée.",
      },
    ],
    warnings: [
      'Un rapprochement validé est verrouillé. Pour modifier, déverrouillez la période concernée.',
    ],
  },

  '/accounting/vat': {
    title: 'Déclarations TVA',
    summary:
      'Déclarations mensuelles de TVA destinées à la DGID. Calcul automatique de la TVA collectée (ventes) moins TVA déductible (achats).',
    fields: [
      {
        name: 'TVA collectée',
        definition: "TVA facturée aux clients (compte 445810). Due à l'État.",
      },
      {
        name: 'TVA déductible',
        definition: 'TVA payée aux fournisseurs (compte 445660). Récupérable sur la TVA collectée.',
      },
      {
        name: 'TVA due',
        definition:
          "TVA collectée — TVA déductible. Si positif : à reverser à l'État. Si négatif : crédit reportable.",
      },
      {
        name: 'Crédit de TVA',
        definition:
          'TVA déductible > TVA collectée. Reporté sur la déclaration suivante ou demande de remboursement (sous conditions).',
      },
    ],
    faq: [
      {
        q: 'Quand déposer la déclaration TVA ?',
        a: 'Au plus tard le 15 du mois suivant. Au Sénégal, taux standard 18% (compte 445810).',
      },
    ],
    warnings: [
      'Une déclaration validée est figée. Toute correction passe par une déclaration rectificative.',
    ],
  },

  '/accounting/accounts': {
    title: 'Grand livre — Compte',
    summary:
      "Détail des mouvements d'un compte comptable : toutes les écritures avec solde progressif. Permet de tracer l'origine de chaque mouvement.",
    fields: [
      {
        name: 'Solde progressif',
        definition: "Solde du compte après chaque écriture (cumul depuis l'ouverture).",
      },
    ],
    faq: [
      {
        q: "Comment voir l'écriture source ?",
        a: "Cliquer sur la ligne → ouvre l'écriture complète (toutes les contreparties).",
      },
    ],
  },

  // ============================================================
  // FINANCE — additions
  // ============================================================

  '/finance/ar': {
    title: 'Créances clients (AR)',
    summary:
      "Suivi des factures impayées et de l'âge des créances. Permet de cibler les actions de relance.",
    fields: [
      {
        name: 'Balance âgée',
        definition: 'Répartition des créances par tranche : 0-30j, 31-60j, 61-90j, 90j+.',
      },
      {
        name: 'DSO',
        definition:
          'Days Sales Outstanding — délai moyen de paiement client. Indicateur clé de la trésorerie.',
      },
    ],
    faq: [
      {
        q: 'Comment lancer une relance ?',
        a: 'Sélectionner les factures concernées → "Relancer". Email automatique au client avec PDF de la facture.',
      },
    ],
  },

  '/finance/vendor-bills': {
    title: 'Factures fournisseurs',
    summary:
      "Saisie et validation des factures reçues des fournisseurs. Génère l'engagement comptable et alimente l'échéancier de paiement.",
    steps: [
      {
        label: 'Saisir la facture',
        detail: 'Fournisseur, numéro, date, montants HT/TVA/TTC, échéance.',
      },
      {
        label: 'Joindre le PDF',
        detail: 'Upload du document original (obligatoire pour archivage).',
      },
      {
        label: 'Valider',
        detail: "Génère l'écriture (DR 60x/61x... / CR 401 / DR 445660 TVA).",
      },
      {
        label: 'Programmer le paiement',
        detail: "À l'échéance, créer un paiement fournisseur.",
      },
    ],
    fields: [
      {
        name: '3-way match',
        definition:
          'Vérification croisée Bon de commande / Bon de réception / Facture avant validation.',
      },
    ],
    warnings: [
      'Une facture validée crée une dette au compte 401. Vérifiez les montants AVANT validation.',
    ],
  },

  // ============================================================
  // BOOKINGS / QUOTES / SHIPMENTS
  // ============================================================

  '/quotes': {
    title: 'Devis',
    summary:
      'Liste des devis émis. Un devis accepté se transforme automatiquement en réservation (booking).',
    steps: [
      {
        label: 'Créer un devis',
        detail: '"Nouveau devis" → client, marchandise, conditions, prix.',
      },
      { label: 'Envoyer au client', detail: 'PDF généré + email automatique.' },
      {
        label: 'Suivre la décision',
        detail: 'Statuts : brouillon, envoyé, accepté, refusé, expiré.',
      },
      { label: 'Conversion', detail: "À l'acceptation, génération automatique d'un booking." },
    ],
    fields: [
      {
        name: 'Validité',
        definition: 'Durée pendant laquelle le devis engage votre prix. Au-delà, statut "expiré".',
      },
    ],
  },

  '/quotes/new': {
    title: 'Nouveau devis',
    summary: "Création d'un devis : client, type de prestation, marchandise, montants, validité.",
    steps: [
      { label: 'Sélectionner le client', detail: 'Existant ou créer à la volée.' },
      {
        label: 'Décrire la prestation',
        detail: 'Origine, destination, type de marchandise, incoterm.',
      },
      {
        label: 'Saisir les lignes de prix',
        detail: 'Fret, douane, manutention, etc. Chaque ligne séparée pour la TVA.',
      },
      { label: 'Définir la validité', detail: "Date limite d'acceptation (par défaut 30 jours)." },
    ],
  },

  '/bookings': {
    title: 'Réservations',
    summary:
      "Liste des réservations actives. Une réservation est créée à l'acceptation d'un devis et déclenche le cycle d'expédition.",
    fields: [
      {
        name: 'Référence booking',
        definition: 'Format BK-YYYY-MM-XXXXX. Identifiant unique transmis au client.',
      },
    ],
    relatedLinks: [
      { label: 'Devis', path: '/quotes' },
      { label: 'Expéditions', path: '/shipments' },
    ],
  },

  '/shipments': {
    title: 'Expéditions',
    summary:
      "Suivi des expéditions en cours et historiques. Statuts du booking jusqu'à la livraison + facturation.",
    fields: [
      { name: 'Status pickup', definition: "Marchandise enlevée chez l'expéditeur." },
      { name: 'Status in_transit', definition: 'En cours de transport (terre/mer/air).' },
      { name: 'Status customs', definition: 'En procédure douanière à destination.' },
      { name: 'Status delivered', definition: 'Livrée. Déclenche la facturation finale.' },
    ],
    relatedLinks: [{ label: 'Carte temps réel', path: '/shipments/map' }],
  },

  '/shipments/map': {
    title: 'Carte des expéditions',
    summary:
      'Visualisation temps réel des positions des véhicules et des expéditions en cours sur une carte.',
    faq: [
      {
        q: "La position d'un véhicule n'est pas à jour",
        a: "Le tracking dépend de l'app chauffeur (Capacitor). Vérifiez que le chauffeur est connecté et a autorisé la géolocalisation.",
      },
    ],
  },

  // ============================================================
  // CUSTOMS
  // ============================================================

  '/customs': {
    title: 'Dédouanement',
    summary:
      'Du dossier à la note de détail : devise et taux, montants, articles, liquidation, puis le PDF.',
    steps: [
      {
        label: 'Créer le dossier',
        detail: 'Choisir la devise FOB et le taux vers le XOF. En XOF, le taux vaut 1.',
      },
      {
        label: 'Saisir les montants',
        detail: 'FOB, fret et assurance en devise d’origine. Le XOF se calcule tout seul.',
      },
      {
        label: 'Ajouter les articles',
        detail: 'Même devise que le dossier, puis lancer la liquidation.',
      },
      { label: 'Lire les taxes', detail: 'Le détail de l’article reprend chaque taxe.' },
      {
        label: 'Exporter la note de détail',
        detail: 'Les colonnes du PDF suivent le même ordre que l’écran.',
      },
    ],
    relatedLinks: [
      { label: 'Dossiers', path: '/customs/dossiers' },
      { label: 'Simulateur', path: '/customs/simulation' },
    ],
  },

  '/customs/dossiers': {
    title: 'Dossiers douane',
    summary: 'Chaque dossier fixe la devise des montants et le taux utilisé pour le XOF.',
    steps: [
      { label: 'Nouveau dossier', detail: 'Renseigner l’importateur, puis la devise FOB.' },
      {
        label: 'Taux vers le XOF',
        detail:
          'Obligatoire si la devise n’est pas le XOF. En XOF, le champ est masqué et le taux vaut 1.',
      },
      {
        label: 'Ouvrir le dossier',
        detail: 'Les articles et la note de détail reprennent cette devise et ce taux.',
      },
    ],
    warnings: [
      'Un taux manquant ou nul bloque l’enregistrement dès qu’un montant en devise d’origine est saisi.',
    ],
  },

  '/customs/declaration': {
    title: 'Déclaration',
    summary:
      'Fiche du dossier. Les montants, les articles et la liquidation s’ouvrent depuis cet écran.',
    steps: [
      {
        label: 'Vérifier la devise',
        detail: 'Elle vient de la création du dossier : {{currency}}, taux {{rate}}.',
      },
      { label: 'Saisir les montants du dossier', detail: 'Modifier le dossier, bloc des valeurs.' },
      { label: 'Ajouter les articles', detail: 'FOB, fret et assurance dans la même devise.' },
      { label: 'Calculer', detail: 'La liquidation alimente la note de détail.' },
    ],
    lockedSteps: [
      { label: 'Dossier clos', detail: 'Les montants et les articles ne se modifient plus.' },
      { label: 'Consulter la liquidation', detail: 'Sélectionner un article pour lire les taxes.' },
      {
        label: 'Exporter la note de détail',
        detail:
          'Le PDF reprend FOB FCFA, CAF, puis DD, RS, PCS, PCC, PROMAD, DA, TVA, acompte BIC, DE, PCF, Autres et le total.',
      },
    ],
  },

  'customs.declaration': {
    title: 'Déclaration',
    summary: 'Fiche du dossier en {{currency}}. Les montants XOF suivent le taux {{rate}}.',
    steps: [
      { label: 'Vérifier la devise', detail: 'Devise {{currency}}, taux {{rate}} vers le XOF.' },
      { label: 'Saisir les montants du dossier', detail: 'Ouvrir Modifier le dossier.' },
      { label: 'Ajouter les articles', detail: 'Même devise, puis calculer la liquidation.' },
      {
        label: 'Exporter la note de détail',
        detail:
          'Colonnes : FOB FCFA, CAF, DD, RS, PCS, PCC, PROMAD, DA, TVA, acompte BIC, DE, PCF, Autres, total.',
      },
    ],
    lockedSteps: [
      { label: 'Dossier clos', detail: 'Consultation et export seulement.' },
      { label: 'Lire un article', detail: 'Le détail des taxes est à droite de la liste.' },
      { label: 'Note de détail', detail: 'Le PDF reprend les mêmes colonnes que la liquidation.' },
    ],
  },

  'customs.declaration.values': {
    title: 'Montants du dossier',
    summary:
      'FOB, fret et assurance se saisissent en {{currency}}. Le XOF affiché vaut le montant arrondi fois le taux {{rate}}.',
    steps: [
      { label: 'Saisir le montant d’origine', detail: 'FOB, fret ou assurance en {{currency}}.' },
      {
        label: 'Lire le XOF',
        detail: 'Exemple : 10,50 {{currency}} × {{rate}} = {{example}} XOF.',
      },
      {
        label: 'Enregistrer',
        detail: 'Le serveur recalcule le même arrondi. Le CAF attendu suit les totaux XOF.',
      },
    ],
    warnings: ['Sans taux supérieur à 0, un montant d’origine ne peut pas être enregistré.'],
    faq: [
      {
        q: 'Le dossier est en XOF',
        a: 'Le taux vaut 1. Le montant saisi est déjà le montant XOF.',
      },
      {
        q: 'Un ancien dossier n’a pas de montant d’origine',
        a: 'Le XOF déjà enregistré est conservé tant qu’aucun montant d’origine n’est saisi.',
      },
    ],
    lockedSteps: [
      {
        label: 'Montants figés',
        detail: 'Le dossier est validé. Les valeurs ne se modifient plus.',
      },
    ],
  },

  'customs.declaration.values.xof': {
    title: 'Montants du dossier',
    summary: 'Le dossier est en XOF. Le taux vaut 1 : le montant saisi est le montant du dossier.',
    steps: [
      {
        label: 'Saisir FOB, fret et assurance',
        detail: 'Les champs XOF reprennent le même nombre.',
      },
      { label: 'Enregistrer', detail: 'Aucun taux de change n’est demandé.' },
    ],
    faq: [{ q: 'Pourquoi le taux n’apparaît pas ?', a: 'En XOF il est masqué et forcé à 1.' }],
    lockedSteps: [
      {
        label: 'Montants figés',
        detail: 'Le dossier est validé. Les valeurs ne se modifient plus.',
      },
    ],
  },

  'customs.declaration.article': {
    title: 'Article',
    summary:
      'FOB, fret et assurance de l’article sont dans la devise du dossier ({{currency}}), convertis en XOF au taux {{rate}}.',
    steps: [
      {
        label: 'Saisir les trois montants',
        detail: 'En {{currency}}. Le XOF à côté est en lecture seule.',
      },
      { label: 'Vérifier l’exemple', detail: '10,50 {{currency}} × {{rate}} = {{example}} XOF.' },
      { label: 'Calculer', detail: 'La liquidation utilise les montants XOF.' },
      { label: 'Enregistrer', detail: 'Le serveur réécrit le XOF avec le taux du dossier.' },
    ],
    warnings: ['Changer le taux du dossier impose de recalculer les articles.'],
    faq: [
      {
        q: 'L’article n’a que des montants XOF',
        a: 'Il se rouvre tel quel. Le XOF n’est pas écrasé tant qu’un montant en devise d’origine n’est pas saisi.',
      },
      {
        q: 'Le taux du dossier manque',
        a: 'L’exemple utilise 600. L’enregistrement d’un montant d’origine est refusé tant que le taux n’est pas renseigné.',
      },
    ],
    lockedSteps: [
      {
        label: 'Article en lecture',
        detail: 'Le dossier est clos. Ouvrez la liquidation pour lire les taxes.',
      },
    ],
  },

  'customs.declaration.article.xof': {
    title: 'Article',
    summary: 'Le dossier est en XOF. FOB, fret et assurance se saisissent directement en XOF.',
    steps: [
      {
        label: 'Saisir les trois montants',
        detail: 'Le taux vaut 1, le montant XOF est le même nombre.',
      },
      {
        label: 'Calculer puis enregistrer',
        detail: 'La liquidation et la note de détail partent de ces montants.',
      },
    ],
    faq: [
      { q: 'Où est la conversion ?', a: 'Il n’y en a pas. La devise du dossier est déjà le XOF.' },
    ],
    lockedSteps: [{ label: 'Article en lecture', detail: 'Le dossier est clos.' }],
  },

  'customs.declaration.liquidation': {
    title: 'Liquidation de l’article',
    summary:
      'Chaque taxe de l’article, puis le total D&T. La note de détail reprend ces montants en colonnes.',
    steps: [
      { label: 'Lire la ligne', detail: 'Code, libellé, taux et montant XOF.' },
      {
        label: 'Retrouver la note de détail',
        detail:
          'Ordre des colonnes : FOB FCFA, CAF, DD, RS, PCS, PCC, PROMAD, DA, TVA, acompte BIC, DE, PCF, Autres, total D&T.',
      },
      {
        label: 'DA et DE',
        detail:
          'DA regroupe les accises. DE est le droit d’enregistrement. Le reste inconnu va dans Autres.',
      },
    ],
    warnings: [
      'Après un changement de taux ou de montant, l’article doit être recalculé avant la note de détail.',
    ],
    lockedSteps: [
      { label: 'Consulter', detail: 'La liquidation est figée avec le dossier.' },
      {
        label: 'Exporter',
        detail: 'La note de détail reprend les colonnes dans l’ordre affiché ici.',
      },
    ],
  },

  '/customs/simulation': {
    title: 'Simulateur',
    summary:
      'Estimation hors dossier. FOB, fret et assurance se saisissent directement en XOF, sans devise ni taux.',
    steps: [
      { label: 'Code SH et origine', detail: 'Ils déterminent les droits.' },
      {
        label: 'Montants en XOF',
        detail: 'Pas de conversion. Pour un dossier en devise, utilisez le formulaire article.',
      },
      { label: 'Lire la liquidation', detail: 'Le résultat ne s’enregistre pas sur un dossier.' },
    ],
  },

  '/customs/circuit': {
    title: 'Circuit douanier',
    summary: 'Suivi des étapes du dossier, du dépôt jusqu’à la mainlevée.',
    steps: [
      { label: 'Repérer l’étape en cours', detail: 'Chaque dossier avance dans le circuit.' },
      {
        label: 'Ouvrir la déclaration',
        detail: 'Les montants et la liquidation se traitent sur la fiche dossier.',
      },
    ],
  },

  '/customs/reports': {
    title: 'Rapports douane',
    summary: 'Synthèses des dossiers et des liquidations déjà calculées.',
    steps: [
      { label: 'Choisir la période', detail: 'Les montants affichés sont en XOF.' },
      {
        label: 'Revenir au dossier',
        detail: 'Le détail des taxes et la note de détail sont sur la déclaration.',
      },
    ],
  },

  // ============================================================
  // WAREHOUSE
  // ============================================================

  '/warehouse': {
    title: 'Entrepôt',
    summary:
      'Gestion du stock, des emplacements et des mouvements (entrées/sorties). Inventaires périodiques.',
    relatedLinks: [
      { label: 'Stock', path: '/warehouse/stock' },
      { label: 'Inventaires', path: '/warehouse/inventory' },
    ],
  },

  '/warehouse/stock': {
    title: 'Stock',
    summary:
      'Liste des articles en stock avec quantités, emplacements et valeurs. Filtres par dépôt ou catégorie.',
    fields: [
      {
        name: 'Quantité disponible',
        definition: 'Stock physique — quantités réservées (en attente de sortie).',
      },
      { name: 'Valorisation PMP', definition: 'Prix Moyen Pondéré. Méthode SYSCOHADA standard.' },
    ],
  },

  '/warehouse/stock/move-in': {
    title: 'Entrée en stock',
    summary: 'Réception de marchandise : crée une entrée stock et alimente le compte 31x au bilan.',
    steps: [
      {
        label: 'Article et quantité',
        detail: "Sélectionner l'article + saisir la quantité reçue.",
      },
      { label: 'Emplacement', detail: 'Préciser le dépôt et la zone (allée/rack).' },
      {
        label: 'Coût unitaire',
        detail: "Pour la valorisation PMP. Inclut tous les frais d'acquisition.",
      },
    ],
  },

  '/warehouse/inventory': {
    title: 'Inventaires',
    summary:
      'Inventaires physiques périodiques. Comparaison stock théorique vs stock compté → écarts à régulariser.',
    warnings: [
      "Un écart d'inventaire validé génère une écriture de régularisation au compte 603x ou 71x.",
    ],
  },

  // ============================================================
  // CRM CLIENTS
  // ============================================================

  '/customers': {
    title: 'Clients',
    summary:
      'Base clients : coordonnées, conditions de paiement, encours, historique des bookings et factures.',
    fields: [
      {
        name: 'Encours autorisé',
        definition: 'Plafond de créances accepté avant blocage des nouvelles commandes.',
      },
      { name: 'Délai de paiement', definition: 'Conditions négociées : 0j, 30j fin de mois, etc.' },
    ],
  },

  // ============================================================
  // SUPPLIERS — Registre fournisseurs (M10)
  // ============================================================

  '/suppliers': {
    title: 'Fournisseurs',
    summary:
      "Registre des fournisseurs d'AMA : transporteurs, courtiers en douane, assureurs, garages, bailleurs… Chaque fournisseur passe par un cycle de qualification avant de pouvoir recevoir des factures fournisseurs.",
    steps: [
      {
        label: 'Créer un fournisseur',
        detail:
          'Bouton « Nouveau fournisseur ». Renseigner la raison sociale (obligatoire), NINEA (14 chiffres), catégorie, contact et délai de paiement. Le fournisseur démarre en statut « En qualification ».',
      },
      {
        label: 'Qualifier',
        detail:
          "Ouvrir la fiche → bouton « Qualifier ». Le fournisseur passe en « Qualifié » et devient sélectionnable lors de la saisie d'une facture fournisseur.",
      },
      {
        label: 'Suspendre / Réactiver',
        detail:
          'Une suspension bloque les nouvelles factures sans archiver le fournisseur. La réactivation est immédiate. Un motif est obligatoire pour la suspension.',
      },
      {
        label: 'Mettre sur liste noire',
        detail:
          'Action terminale : le fournisseur ne peut plus être réutilisé. Réservée aux cas graves (litige, fraude). Motif obligatoire, conservé pour audit.',
      },
    ],
    fields: [
      {
        name: 'NINEA',
        definition: '14 chiffres. Identifiant fiscal sénégalais. Validation stricte côté backend.',
      },
      {
        name: 'Catégorie',
        definition:
          'Carrier, customs_broker, insurer, fuel, maintenance, it_services, professional, utilities, rent, security, other. Sert au reporting AP et au routage comptable.',
      },
      {
        name: 'Statut de qualification',
        definition:
          'in_qualification → qualified → suspended → blacklisted. Seul un fournisseur « qualified » peut recevoir une nouvelle facture.',
      },
      {
        name: 'Délai de paiement',
        definition:
          'Délai par défaut copié sur les factures émises par ce fournisseur (0/15/30/45/60/90 jours).',
      },
      {
        name: 'Note moyenne',
        definition: 'Recalculée automatiquement à chaque nouvelle évaluation périodique (0 à 5).',
      },
    ],
    warnings: [
      "Une facture fournisseur saisie avec un fournisseur non-qualifié est rejetée par l'API (409 SUPPLIER_NOT_QUALIFIED).",
      'La mise sur liste noire est terminale : aucune transition de retour possible.',
    ],
    relatedLinks: [
      { label: 'Factures fournisseurs', path: '/finance/vendor-bills' },
      { label: 'Paiements fournisseurs', path: '/finance/vendor-payments' },
    ],
    faq: [
      {
        q: "Pourquoi mon fournisseur n'apparaît pas dans le picker des factures ?",
        a: 'Le picker filtre par défaut sur les fournisseurs qualifiés uniquement. Vérifier le statut sur sa fiche détail et qualifier si nécessaire.',
      },
      {
        q: 'Comment renseigner les coordonnées bancaires ?',
        a: "Édition du fournisseur → section « Coordonnées bancaires ». IBAN, SWIFT, mobile money (Wave / OM) sont supportés. Le N° de compte sert lors de la saisie d'un paiement fournisseur.",
      },
      {
        q: 'Quelle différence avec « Libellé fournisseur (legacy) » sur les factures ?',
        a: 'Le libellé legacy est un texte libre conservé pour rétro-compatibilité (factures saisies avant M10). Les nouvelles factures utilisent le supplier_id du registre.',
      },
    ],
  },

  '/customers/new': {
    title: 'Nouveau client',
    summary:
      "Création d'un dossier client : informations légales, fiscales, conditions commerciales.",
    steps: [
      { label: 'Identité', detail: 'Raison sociale, NINEA, RC.' },
      { label: 'Coordonnées', detail: 'Adresse, contact principal, email facturation.' },
      { label: 'Conditions', detail: 'Délai paiement, encours autorisé, devise par défaut.' },
    ],
  },

  // ============================================================
  // EXPENSES (notes de frais)
  // ============================================================

  '/expenses': {
    title: 'Notes de frais',
    summary:
      'Gestion des notes de frais : soumission par les employés, validation managériale, remboursement.',
    relatedLinks: [
      { label: 'Mes notes', path: '/expenses/claims' },
      { label: 'Catégories', path: '/expenses/categories' },
    ],
  },

  '/expenses/claims': {
    title: 'Mes notes de frais',
    summary: 'Liste de vos notes soumises avec leur statut. Filtres par période et statut.',
    fields: [
      { name: 'Statut soumis', definition: 'En attente de validation par le manager.' },
      { name: 'Statut validé', definition: 'Approuvé. En attente de remboursement.' },
      { name: 'Statut remboursé', definition: "Payé sur le compte de l'employé." },
    ],
  },

  '/expenses/claims/new': {
    title: 'Nouvelle note de frais',
    summary:
      "Soumission d'une dépense pour remboursement : montant, catégorie, justificatif obligatoire.",
    steps: [
      { label: 'Date et montant', detail: 'Date de la dépense (pas de soumission), montant TTC.' },
      { label: 'Catégorie', detail: 'Repas, transport, hébergement, fournitures, etc.' },
      { label: 'Justificatif', detail: 'Photo du reçu (obligatoire au-delà de 5 000 XOF).' },
      { label: 'Soumettre', detail: 'Notification automatique au manager.' },
    ],
  },

  '/expenses/categories': {
    title: 'Catégories de dépenses',
    summary:
      'Configuration des catégories utilisées pour les notes de frais. Chaque catégorie est mappée à un compte SYSCOHADA.',
  },

  // ============================================================
  // HR — additions
  // ============================================================

  '/hr/analytics': {
    title: 'Analytique RH',
    summary:
      "KPIs RH : effectif, turnover, ancienneté moyenne, taux d'absentéisme, pyramide des âges.",
  },

  '/hr/documents': {
    title: 'Documents RH',
    summary:
      'Bibliothèque de documents RH : règlement intérieur, conventions, procédures. Visibles par tous les employés selon les droits.',
  },

  '/hr/contracts/new': {
    title: 'Nouveau contrat',
    summary:
      "Création d'un contrat de travail. Lié à un employé existant. Définit le salaire, le poste et les conditions.",
    fields: [
      { name: 'Date de début', definition: "Premier jour effectif. Détermine l'ancienneté." },
      {
        name: "Période d'essai",
        definition: '1 à 3 mois selon la catégorie. Renouvelable une fois.',
      },
      {
        name: 'Salaire de base',
        definition: 'Brut mensuel. Base de calcul des cotisations sociales.',
      },
    ],
  },

  '/hr/leaves/new': {
    title: 'Nouvelle demande de congé',
    summary:
      "Création d'une demande pour un employé (côté admin). Pour le self-service employé, voir Mes congés.",
  },

  '/hr/leaves/calendar': {
    title: 'Calendrier des congés',
    summary:
      "Vue calendrier des absences de tous les employés. Permet d'anticiper les périodes critiques (low staff).",
  },

  // ============================================================
  // MY — additions
  // ============================================================

  '/my/leaves/new': {
    title: 'Demander un congé',
    summary: "Soumission d'une demande de congé. Le solde disponible est affiché en temps réel.",
    steps: [
      { label: 'Type de congé', detail: 'Payés, maladie, événement familial, sans solde.' },
      { label: 'Dates', detail: 'Calcul automatique des jours ouvrables (hors WE et fériés).' },
      { label: 'Motif', detail: 'Obligatoire pour certains types.' },
      { label: 'Soumettre', detail: 'Notification au manager. Statut "En attente".' },
    ],
  },

  '/my/documents': {
    title: 'Mes documents',
    summary:
      'Vos documents personnels : contrat, attestations, certificats. Téléchargeables en PDF.',
  },

  // ============================================================
  // SETTINGS — additions
  // ============================================================

  '/settings/preferences': {
    title: 'Mes préférences',
    summary:
      "Configuration de votre interface : langue, fuseau horaire, format de date, devise d'affichage.",
  },

  '/settings/profile': {
    title: 'Mon profil (paramètres)',
    summary:
      "Informations personnelles : nom, email, téléphone, photo. L'email est utilisé pour les notifications.",
  },

  '/settings/security': {
    title: 'Sécurité',
    summary:
      'Mot de passe, sessions actives, double authentification (2FA). Recommandé pour les comptes admin.',
    warnings: [
      'Activer la 2FA est fortement recommandé pour les comptes ayant accès à des données financières.',
    ],
  },

  '/settings/accounting': {
    title: 'Paramètres comptables',
    summary:
      'Exercice fiscal, journaux comptables, paramètres TVA, comptes par défaut pour les écritures automatiques.',
  },

  '/settings/chart-of-accounts': {
    title: 'Plan comptable',
    summary:
      'Plan de comptes SYSCOHADA. Pré-rempli selon le secteur. Personnalisable (sous-comptes analytiques).',
    warnings: ["La modification ou suppression d'un compte ayant des écritures est bloquée."],
  },

  '/settings/hr': {
    title: 'Paramètres RH',
    summary:
      'Configuration RH : types de congés, soldes annuels, jours fériés, conventions collectives appliquées.',
  },

  '/settings/email': {
    title: 'Paramètres email',
    summary:
      "Configuration du serveur SMTP pour l'envoi des notifications, factures et bulletins par email.",
  },

  '/settings/documents': {
    title: 'Modèles de documents',
    summary:
      'Personnalisation des templates : factures, devis, bulletins, courriers. Logo, mentions légales, signatures.',
  },

  '/settings/tariffs': {
    title: 'Tarifs',
    summary:
      'Grilles tarifaires utilisées dans les devis : prix au kg, au m³, par destination, etc.',
  },

  // ============================================================
  // CRM (M08)
  // ============================================================

  '/crm': {
    title: 'CRM Clients',
    summary:
      "Vue d'ensemble commerciale : segmentation Gold/Silver/Bronze, score crédit, clients à risque churn et actions à mener.",
    faq: [
      {
        q: 'Comment est calculé le score crédit ?',
        a: 'Score 0-100 basé sur 3 critères pondérés : délai moyen de paiement (40%), nombre de factures en retard (35%), ancienneté client (25%). Les poids sont ajustables dans CRM → Paramètres.',
      },
      {
        q: 'Quand les segments sont-ils recalculés ?',
        a: 'Tous les jours à 06:00 UTC pour le mois en cours, et immédiatement à chaque paiement reçu ou expédition livrée.',
      },
      {
        q: 'Quels modules alimentent le scoring ?',
        a: 'Les expéditions livrées (M02) pour la fenêtre churn, les factures + paiements (M03) pour le revenu TTM et les retards. Pas de pré-requis sur les modules optionnels.',
      },
    ],
  },

  '/crm/customers': {
    title: 'Clients (vue CRM)',
    summary:
      'Liste enrichie avec segmentation, scoring crédit et indicateur de churn. Filtres rapides par tier et par niveau de risque.',
    fields: [
      {
        name: 'Tier (Gold / Silver / Bronze)',
        definition:
          'Segment calculé selon les revenus des 12 derniers mois. Seuils configurables dans CRM → Paramètres.',
      },
      {
        name: 'Score crédit',
        definition:
          'Indice de fiabilité financière de 0 (risqué) à 100 (excellent). Combine délai de paiement, retards, ancienneté.',
      },
      {
        name: 'Churn risk',
        definition:
          'Faible = client actif, Modéré = en retrait, Élevé = action urgente requise (relance commerciale, geste).',
      },
    ],
  },

  '/crm/churn-risk': {
    title: 'Clients à risque',
    summary:
      "Liste filtrée des clients en risque de churn élevé. Ouvrez la fiche pour voir l'historique des interactions et déclencher une relance.",
    faq: [
      {
        q: 'Pourquoi un client passe-t-il en risque élevé ?',
        a: "Soit pas d'expédition depuis ≥ 90 jours (configurable), soit une baisse de revenu de plus de 50% versus l'année précédente.",
      },
    ],
  },

  '/crm/settings': {
    title: 'Paramètres CRM',
    summary:
      'Seuils de segmentation, fenêtres churn, pondérations du score crédit. Toute modification impacte le prochain recalcul.',
    warnings: [
      'La somme des trois pondérations doit être égale à 100.',
      'Le seuil Gold doit être strictement supérieur au seuil Silver.',
    ],
  },
}

// Helper : trouver le contenu pour une route donnée
// (match exact, puis prefix le plus long)
//
// Locale-aware: when the active i18n language is EN, prefer the English
// map. If no EN entry exists for the route, fall back to FR so the help
// is never empty — partial EN coverage degrades gracefully.
export interface HelpLookup {
  key?: string
  vars?: Record<string, string>
}

function lookupHelp(
  map: Record<string, HelpPage>,
  pathname: string,
  key?: string,
): HelpPage | null {
  if (key && map[key]) return map[key]
  if (map[pathname]) return map[pathname]
  const match = Object.keys(map)
    .filter(
      (entry) => entry.startsWith('/') && (pathname.startsWith(`${entry}/`) || pathname === entry),
    )
    .sort((a, b) => b.length - a.length)[0]
  return match ? (map[match] ?? null) : null
}

function fill(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_, name: string) => vars[name] ?? '')
}

function applyHelpVars(page: HelpPage, vars: Record<string, string>): HelpPage {
  const locked = vars.locked === '1' && page.lockedSteps?.length
  return {
    ...page,
    summary: fill(page.summary, vars),
    steps: (locked ? page.lockedSteps : page.steps)?.map((step) => ({
      label: fill(step.label, vars),
      detail: step.detail ? fill(step.detail, vars) : undefined,
    })),
    fields: page.fields?.map((field) => ({
      name: fill(field.name, vars),
      definition: fill(field.definition, vars),
    })),
    faq: page.faq?.map((item) => ({ q: fill(item.q, vars), a: fill(item.a, vars) })),
    warnings: page.warnings?.map((item) => fill(item, vars)),
  }
}

export function getHelpContent(
  pathname: string,
  scope?: HelpLookup,
  language: string = i18n.language,
): HelpPage | null {
  const isEn = language?.startsWith('en') ?? false
  const primary = isEn ? HELP_CONTENT_EN : HELP_CONTENT
  const fallback = isEn ? HELP_CONTENT : null

  const page =
    lookupHelp(primary, pathname, scope?.key) ??
    (fallback ? lookupHelp(fallback, pathname, scope?.key) : null)
  if (!page) return null
  return scope?.vars ? applyHelpVars(page, scope.vars) : page
}

// Règle de maintenance :
// À chaque nouvelle session de dev, ajouter les routes
// des nouveaux modules dans cet objet.
// Modules à ajouter après finalisation AMA :
//   /quotes, /shipments, /customs, /warehouse,
//   /fleet, /customers, /suppliers
